/* Settings: the questions that are about the tool rather than about the
   portfolio.

   People lives here rather than in the top-level navigation because it is
   consulted rarely -- when somebody joins, leaves, or takes over an object --
   while the other four tabs are read every week. A tab that is right most of
   the time and a tab that is right once a quarter should not sit side by side.

   As stage 2 lands, this is where user access and roles go, next to the
   registry they join to. */
import { useCallback, useEffect, useRef, useState } from "react";

import { api, type AccessRow } from "../api";
import { usePermissions } from "../auth";
import PeoplePanel from "../components/PeoplePanel";
import { Panel } from "../components/ui";
import { useStore } from "../store";

export default function Settings() {
  const { meta, services, assets, tasks } = useStore();
  const { isAdmin } = usePermissions();

  return (
    <>
      {isAdmin && <AccessPanel />}
      <PeoplePanel />

      <Panel title="This instance">
        <div className="tablewrap">
          <table className="wrap">
            <tbody>
              <tr>
                <td className="left">Sign-in</td>
                <td className="left">
                  {meta?.auth_enabled ? (
                    `Google, restricted to ${(meta.allowed_domains ?? [])
                      .map((d) => `@${d}`)
                      .join(" and ")}`
                  ) : (
                    <>
                      <strong>Demo mode — nobody signs in.</strong> Every request is treated as
                      admin, and only requests from this machine are answered at all. Task log
                      entries are attributed to <code>{meta?.me.email}</code> rather than to a
                      person, so the log never claims someone was responsible for a change made
                      without authentication.
                    </>
                  )}
                </td>
              </tr>
              <tr>
                <td className="left">Schema version</td>
                <td className="left">{meta?.schema_version}</td>
              </tr>
              <tr>
                <td className="left">Contents</td>
                <td className="left">
                  {services.length} services · {assets.length} assets · {tasks.length} tasks
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Backup">
        <p className="note">
          Every row in this tool was typed by a person and exists nowhere else — unlike the
          BizDev monitor, whose database can be rebuilt by re-syncing HubSpot. A dated copy is
          taken automatically on every startup, before migrating rather than after. This is the
          copy you can take away.
        </p>
        <p className="row">
          <a className="btn" href="/api/export" download="spd-services.json">
            Download everything as JSON
          </a>
          {isAdmin && <RestoreButton />}
        </p>
        <p className="note">
          Every table verbatim, with the schema version it was written against — a restore wants
          rows, not a view of them.
        </p>
      </Panel>
    </>
  );
}

/* Loading a backup back in.

   The file is parsed and counted in the browser before anything is sent, so a
   file that is not a backup is refused without the server having touched the
   database, and so the confirmation can say what is in the file rather than
   asking for a blind yes. */
function RestoreButton() {
  const { run, meta } = useStore();
  const input = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<string | null>(null);

  const chosen = async (file: File) => {
    setResult(null);
    let payload: { tables?: Record<string, unknown[]> };
    try {
      payload = JSON.parse(await file.text());
    } catch {
      setResult(`${file.name} is not valid JSON.`);
      return;
    }
    const count = (name: string) => payload.tables?.[name]?.length ?? 0;
    /* Counted fresh rather than from what this page last loaded. The dialog is
       the last thing standing between a click and an irreversible replacement,
       and a stale "now" line is exactly the sentence somebody would read to
       decide they are replacing nothing important. */
    const [now, nowAssets, nowTasks] = await Promise.all([
      api.list("services"),
      api.list("assets"),
      api.tasks(),
    ]);
    const confirmed = window.confirm(
      `Replace everything in the tracker with ${file.name}?

` +
        `In the file: ${count("services")} services, ${count("assets")} assets, ` +
        `${count("tasks")} tasks, ${count("people")} people.
` +
        `In the tracker now: ${now.length} services, ${nowAssets.length} assets, ` +
        `${nowTasks.length} tasks.

` +
        `Everything currently here is deleted. A timestamped copy of the current ` +
        `database is saved to the backups folder first.`,
    );
    if (!confirmed) return;

    const summary = await run(() => api.importBackup(payload));
    if (summary) {
      const rows = Object.values(summary.loaded).reduce((a, b) => a + b, 0);
      /* An older backup can need its shape changed on the way in, and one of
         those changes can drop an attachment. Said here rather than left in
         the server's response, because this screen is the only place anyone
         sees the result of a restore. */
      const upgraded = (summary.upgraded ?? []).join("; ");
      setResult(
        `Restored ${rows} rows from ${file.name}` +
          (summary.schema_version < (meta?.schema_version ?? summary.schema_version)
            ? ` (written at schema ${summary.schema_version}, upgraded to ` +
              `${meta?.schema_version})`
            : "") +
          `. ${upgraded ? upgraded + ". " : ""}Previous database saved to ` +
          `${summary.previous_database_saved_to ?? "the backups folder"}.`,
      );
    }
  };

  return (
    <>
      <label className="btn">
        Restore from a file…
        <input
          ref={input}
          type="file"
          accept="application/json,.json"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            /* Cleared so that choosing the same file twice fires again -- after
               a refused confirmation, the obvious second attempt is the same
               file, and an input that has not changed raises no event. */
            e.target.value = "";
            if (file) void chosen(file);
          }}
        />
      </label>
      {result && <span className="muted">{result}</span>}
    </>
  );
}

/* Who may sign in, and as what. Admin only -- the server refuses these routes
   to everybody else, and this panel is simply not drawn for them.

   Separate from People, which sits directly below it. People is who work is
   attributed to; this is who may open the application. The two are joined by
   email when both apply, and plenty of rows belong to only one of them: a
   person imported from the spreadsheet who never signs in, or an admin who
   owns nothing. */
function AccessPanel() {
  const { run, meta } = useStore();
  const [rows, setRows] = useState<AccessRow[] | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("readonly");

  const load = useCallback(async () => {
    const data = await api.access();
    setRows(data.users);
    setRoles(data.roles);
  }, []);

  useEffect(() => {
    void load().catch(() => setRows([]));
  }, [load]);

  const act = async <T,>(action: () => Promise<T>) => {
    const done = await run(action);
    if (done) await load();
  };

  return (
    <Panel title="Access" aside={<span className="muted">{rows?.length ?? 0}</span>}>
      <div className="tablewrap">
        <table className="wrap">
          <thead>
            <tr>
              <th className="left">Email</th>
              <th className="left">Name</th>
              <th className="left">Role</th>
              <th className="left">Status</th>
              <th className="left">Last seen</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(rows ?? []).map((r) => (
              <tr key={r.email}>
                <td className="left">{r.email}</td>
                <td className="left muted">{r.display_name ?? "—"}</td>
                <td className="left">
                  <select
                    value={r.role}
                    onChange={(e) => act(() => api.changeAccess(r.email, { role: e.target.value }))}
                  >
                    {roles.map((option) => (
                      <option key={option}>{option}</option>
                    ))}
                  </select>
                </td>
                <td className="left">
                  <select
                    value={r.status}
                    onChange={(e) =>
                      act(() => api.changeAccess(r.email, { status: e.target.value }))
                    }
                  >
                    <option value="active">active</option>
                    <option value="disabled">disabled</option>
                  </select>
                </td>
                <td className="left muted">
                  {r.last_seen_at ? r.last_seen_at.slice(0, 10) : "never"}
                </td>
                <td>
                  <button
                    className="linkbtn"
                    onClick={() => {
                      if (!confirm(`Remove access for ${r.email}?`)) return;
                      void act(() => api.revoke(r.email));
                    }}
                  >
                    remove
                  </button>
                </td>
              </tr>
            ))}
            {rows !== null && rows.length === 0 && (
              <tr>
                <td className="left muted" colSpan={6}>
                  Nobody is on the access list.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="row" style={{ marginTop: 12 }}>
        <input
          type="text"
          placeholder={`name@${meta?.allowed_domains?.[0] ?? "spd.tech"}`}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          {roles.map((option) => (
            <option key={option}>{option}</option>
          ))}
        </select>
        <button
          className="btn"
          disabled={!email.trim()}
          onClick={() => {
            const address = email.trim();
            setEmail("");
            void act(() => api.grant(address, role));
          }}
        >
          Grant
        </button>
      </div>
    </Panel>
  );
}
